'use client'

import { cva } from 'class-variance-authority'
import { AlertCircle } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import { SpeciesIcon } from '@/customer/components/pet/species-icon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemContent,
  SelectItemDescription,
  SelectItemLabel,
  SelectTrigger,
  SelectValue,
} from '@/customer/components/ui/select'
import { usePetIssues } from '@/customer/hooks/use-pet-issues'
import { cn } from '@/customer/lib/utils'

/**
 * Monogram styles mirror `PetTile`'s so Select items and the trigger
 * value read as members of the same family. Kept local to avoid
 * exporting internals from `pet-tile.tsx`.
 */
const monogramStyles = cva(
  'flex size-9 shrink-0 items-center justify-center rounded-lg border-2 border-foreground text-foreground',
  {
    variants: {
      sex: {
        male: 'bg-baby-blue',
        female: 'bg-pink',
      },
    },
    defaultVariants: { sex: 'female' },
  }
)

type MonogramVariant = 'filled' | 'bare'

function PetRow({
  pet,
  monogramVariant,
  showBreed,
}: {
  pet: PublicPatient
  monogramVariant: MonogramVariant
  showBreed: boolean
}) {
  const { issue } = usePetIssues(pet.id)
  const isLapsed = issue?.type === 'lapsed_subscription'

  return (
    <>
      <span
        className={cn(
          monogramVariant === 'bare'
            ? 'flex shrink-0 items-center justify-center text-foreground'
            : monogramStyles({ sex: pet.sex === 'male' ? 'male' : 'female' }),
          isLapsed && 'opacity-50'
        )}
      >
        <SpeciesIcon
          species={pet.species}
          className={monogramVariant === 'bare' ? 'size-6' : 'size-5'}
          aria-hidden
        />
      </span>
      <SelectItemContent className="flex-initial justify-center">
        <SelectItemLabel
          className={cn(
            'text-body-md font-bold uppercase leading-none',
            isLapsed && 'text-muted-foreground'
          )}
        >
          {pet.name}
        </SelectItemLabel>
        {showBreed && pet.breed ? (
          <SelectItemDescription className="truncate text-fine-print">
            {pet.breed}
          </SelectItemDescription>
        ) : null}
      </SelectItemContent>
      {issue?.type === 'incomplete_subscription' ? (
        <AlertCircle className="size-4 shrink-0 self-center text-warning" aria-hidden />
      ) : null}
    </>
  )
}

/**
 * Select-flavoured pet chooser. Sibling to `PetTile` - same visual
 * language (species-icon monogram + name + optional breed) rendered
 * inside a `Select` for narrow viewports where a tile grid or tab
 * strip is too tall.
 */
export function PetSelect({
  value,
  onValueChange,
  pets,
  monogramVariant = 'filled',
  showBreed = true,
  placeholder = 'Choose a pet',
  className,
  'aria-labelledby': ariaLabelledBy,
}: {
  value: string | null
  onValueChange: (id: string) => void
  pets: PublicPatient[]
  monogramVariant?: MonogramVariant
  showBreed?: boolean
  placeholder?: string
  className?: string
  'aria-labelledby'?: string
}) {
  // Base UI needs `items` on the root so a selected value can resolve
  // to its label without the popup being mounted (see project AGENTS).
  const items = pets.map((pet) => ({ value: pet.id, label: pet.name }))

  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as string)} items={items}>
      <SelectTrigger
        className={cn('h-auto min-h-14 py-2', className)}
        aria-labelledby={ariaLabelledBy}
      >
        <SelectValue placeholder={placeholder}>
          {(label) => {
            const selected = pets.find((p) => p.id === value)
            if (!selected) return label
            return <PetRow pet={selected} monogramVariant={monogramVariant} showBreed={showBreed} />
          }}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {pets.map((pet) => (
          <SelectItem key={pet.id} value={pet.id} className="py-2">
            <PetRow pet={pet} monogramVariant={monogramVariant} showBreed={showBreed} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
